import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../services/api';

const Check = ({ ok, label, detail }) => (
  <li className="flex items-start gap-2 text-sm" data-testid={`deliv-check-${label.toLowerCase()}`}>
    {ok ? <CheckCircle size={16} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={16} className="text-red-600 mt-0.5 shrink-0" />}
    <span><strong>{label}</strong>{detail ? <span className="text-gray-500"> — {detail}</span> : null}</span>
  </li>
);

// Admin › E-mails : why do our mails land in spam? DNS + Hostinger spam verdicts + what the sender now does right
const EmailDeliverabilityCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setData((await api.get('/admin/email-deliverability')).data); } catch { setData({ error: true }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="bg-white rounded-lg shadow-sm p-4 mb-6 text-sm text-gray-500 flex items-center gap-2" data-testid="email-deliverability-card"><Loader2 size={14} className="animate-spin" /> Analyse de la délivrabilité…</div>;
  if (data.error) return null;
  const { dns, mailbox, advice } = data;
  const flagged = mailbox?.flagged || 0;
  const recentOk = (mailbox?.items || []).filter(i => i.own_message_id);
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="email-deliverability-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">{flagged ? <ShieldAlert size={16} className="text-orange-500" /> : <ShieldCheck size={16} className="text-emerald-600" />} Délivrabilité des e-mails (anti-spam)</h3>
          <p className="text-xs text-gray-500" data-testid="email-deliverability-summary">
            Expéditeur {data.sender} · {mailbox?.available ? `${mailbox.total} envoi(s) retrouvés dans la boîte, ${flagged} marqué(s) spam par Hostinger` : `boîte non analysée (${mailbox?.reason || 'IMAP indisponible'})`}
            {recentOk.length ? ` · ${recentOk.length} envoi(s) récents avec les nouveaux en-têtes : ${recentOk.filter(i => i.spam).length} en spam` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1" data-testid="email-deliverability-refresh">{loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} actualiser</button>
          <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-0.5" data-testid="email-deliverability-toggle">{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} détails</button>
        </div>
      </div>
      <ul className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
        <Check ok={dns.spf.ok} label="SPF" detail={dns.spf.record || 'absent'} />
        <Check ok={dns.dkim.ok} label="DKIM" detail={Object.entries(dns.dkim.selectors).filter(([, v]) => v).map(([k]) => k).join(', ') || 'aucun sélecteur actif'} />
        <Check ok={dns.dmarc.ok} label="DMARC" detail={dns.dmarc.record || 'absent'} />
      </ul>
      {open && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="email-deliverability-details">
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Ce que font maintenant les envois</p>
            <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">{(data.headers || []).map(h => <li key={h}>{h}</li>)}<li>Anti-doublon : même objet au même destinataire ignoré pendant 10 min ({data.deduplicated_30d} évité(s) sur 30 j)</li></ul>
            <p className="text-xs font-semibold text-gray-700 mt-3 mb-1">Conseils</p>
            <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1" data-testid="email-deliverability-advice">{(advice || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Derniers envois vus dans la boîte</p>
            <ul className="text-xs divide-y max-h-72 overflow-y-auto border rounded-lg" data-testid="email-deliverability-list">
              {(mailbox?.items || []).slice(0, 25).map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-2 px-2 py-1.5">
                  <span className="truncate text-gray-800">{it.subject}</span>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${it.spam ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{it.spam ? 'spam' : 'boîte de réception'}{it.own_message_id ? ' · nouveau' : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDeliverabilityCard;
